import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import { prop } from 'ramda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
// usePluginRouter removed - currentPath passed as prop to avoid re-renders
import { useSelector } from 'react-redux'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { NestedLink } from 'core/plugins/model'
import { matchesCurrentPath } from 'core/plugins/route-helpers'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'
import Divider from '../Divider'
import NavItem from '../sidebar/NavItem'
import NavSection from '../sidebar/NavSection'
import Text from '../Text'
import { bottomRight } from './defaults'
import Menu, { IMenuProps } from './Menu'

interface Props extends IMenuProps {
  title: string
  icon?: string
  open: boolean
  anchorEl?: JSX.Element
  nestedLinks: NestedLink[]
  onClose: () => void
  disabled?: boolean
  drawerOpen?: boolean
  k8sClusterAvailable?: boolean
  info?: string
  currentPath?: string
  currentPluginId?: string
}

export default React.memo(function NavMenu({
  nestedLinks,
  anchorEl = null,
  open,
  onClose,
  icon,
  title,
  className,
  disabled,
  drawerOpen = false,
  k8sClusterAvailable = false,
  info,
  currentPath = '',
  currentPluginId = '',
  ...props
}: Props) {
  const classes = useStyles()
  // currentPath now passed as prop from parent
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { virtualNetworking } = session
  const memoizedNestedLinks = useMemo(
    () =>
      nestedLinks?.map((navItem, index) =>
        navItem?.isHidden && navItem?.isHidden({ virtualNetworking }) ? null : (
          <NavSection
            key={index}
            {...navItem}
            isLastChild={nestedLinks[nestedLinks.length - 1] === navItem}
            isActive={!!matchesCurrentPath(currentPath, navItem)}
            k8sClusterAvailable={k8sClusterAvailable}
            disabled={navItem?.isk8sNav && !k8sClusterAvailable}
            info={
              info ? info : navItem?.isk8sNav && !k8sClusterAvailable ? 'No Clusters Available' : ''
            }
            drawerOpen={true}
            nestingLevel={2}
            currentPath={currentPath}
            currentPluginId={currentPluginId}
            disableMargin={true}
            isLeaf={(navItem?.nestedLinks ?? []).length === 0}
          />
        ),
      ),
    [nestedLinks, currentPath, onClose, disabled, classes],
  )

  return (
    <Menu
      className={clsx(classes.menuContainer, className)}
      data-testid={generateTestId('nav-section', 'menu')}
      origin="left top"
      anchor={anchorEl}
      open={open}
      align={bottomRight.align}
      onClose={onClose}
      {...props}
    >
      <div className={classes.titleContainer}>
        <Text className={clsx(classes.title, 'menu-title-text')} component="div" variant="sidenav1">
          {title}
        </Text>
        {memoizedNestedLinks}
      </div>
    </Menu>
  )
})

const useStyles = makeStyles<Theme>((theme) => ({
  nestedSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  parentItem: {
    fontWeight: 500,
    '&:hover': {
      background: theme.components.sidebar.border,
    },
  },
  subItem: {
    opacity: 0.9,
    fontSize: '0.95em',
  },
  menuContainer: {
    '& .menu-popover': {
      padding: theme.spacing(2, 0, 2, 1),
      background: theme.palette.grey['000'], // theme.components.sidebar.background,
      borderColor: theme.components.sidebar.border,
      borderRadius: theme.spacing(0.5),
    },

    // Override navItem height when menu popover is active
    '& .menu-popover.active li': {
      height: '32px !important',
    },

    // Set default text color to grey[500] for all nav items in menu popover
    '& .menu-popover .nav-text': {
      color: `${theme.palette.grey[500]} !important`,
    },

    // Active items (detected by data-active attribute) - set to grey[700]
    '& .menu-popover li[data-active="true"] .nav-text': {
      color: `${theme.palette.grey[700]} !important`,
    },

    // Hover state for nav items in menu popover - change to grey[700]
    '& .menu-popover li:hover .nav-text': {
      color: `${theme.palette.grey[700]} !important`,
    },
  },

  titleContainer: {
    padding: theme.spacing(0, 0, 0, 1.5),
  },
  title: {
    display: 'flex',
    padding: theme.spacing(0, 2, 1, 0),
    height: 24,
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.components.sidebar?.activeText,
  },
  nestedNavItem: {
    padding: theme.spacing(0, 3),
    '&:hover': {
      borderRadius: theme.spacing(0.5),
      background: theme.components.sidebar.border,
      transition: 'background 0.2s ease',
    },

    '& .tooltip-container': {
      marginLeft: theme.spacing(2),
    },
  },
}))
